import { useState } from 'react';
import { NavLink, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { useNotifications } from '../lib/NotificationsContext';
import { cn } from '../lib/utils';
import { LanguageSelector } from './LanguageSelector';
import { useTranslation } from 'react-i18next';
import {
  Home,
  Search,
  Trophy,
  Palette,
  ShoppingBag,
  ChevronLeft,
  ChevronRight,
  LogOut,
  User,
  Volume2,
  VolumeX,
  X
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Switch } from './ui/switch';

const navItems = [
  { path: '/', icon: Home, labelKey: 'sidebar.home' },
  { path: '/product-research', icon: Search, labelKey: 'sidebar.productResearch' },
  { path: '/winning-products', icon: Trophy, labelKey: 'sidebar.winningProducts' },
  { path: '/ads', icon: Palette, labelKey: 'sidebar.creativeAds' },
  { path: '/shopify', icon: ShoppingBag, labelKey: 'sidebar.shopify' },
];

export function Sidebar({ collapsed, setCollapsed, isMobile = false, onClose }) {
  const { user, signOut } = useAuth();
  const { soundEnabled, toggleSound } = useNotifications();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // Get user initials
  const getInitials = () => {
    if (!user?.email) return 'U';
    const parts = user.email.split('@')[0].split(/[._-]/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return user.email.slice(0, 2).toUpperCase();
  };

  const handleNavClick = () => {
    if (isMobile && onClose) {
      onClose();
    }
  };

  return (
    <aside
      className={cn(
        'bg-white border-r border-slate-200 flex flex-col h-screen',
        'transition-all duration-300 ease-in-out',
        isMobile ? 'w-64' : (collapsed ? 'w-20 fixed left-0 top-0' : 'w-64 fixed left-0 top-0')
      )}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className={cn(
        'flex items-center h-[65px] border-b border-slate-200',
        collapsed && !isMobile ? 'justify-center px-4' : 'justify-between px-6'
      )}>
        <Link
          to="/"
          onClick={handleNavClick}
          className={cn(
            'flex items-center gap-3',
            collapsed && !isMobile && 'justify-center'
          )}
        >
          <img
            src="https://res.cloudinary.com/dq4ihdqwt/image/upload/v1766574405/Untitled_design_2_jbpydg.png"
            alt="Ecom Factory"
            className="w-10 h-10 flex-shrink-0"
          />
          {(!collapsed || isMobile) && (
            <span className="text-lg font-bold text-[#1E1B4B] font-['Manrope'] whitespace-nowrap">
              {t('app.name')}
            </span>
          )}
        </Link>
        
        {/* Close button for mobile */}
        {isMobile && (
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={handleNavClick}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-4 py-3 rounded-xl transition-all',
                'font-medium text-sm',
                isActive
                  ? 'bg-gradient-to-r from-cyan-500 to-cyan-600 text-white shadow-lg shadow-cyan-500/30'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-[#1E1B4B]',
                collapsed && !isMobile && 'justify-center px-3'
              )
            }
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {(!collapsed || isMobile) && <span>{t(item.labelKey)}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Collapse Toggle - Desktop only */}
      {!isMobile && (
        <div className="p-4 border-t border-slate-200">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-3 rounded-xl',
              'text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors',
              collapsed && 'justify-center px-3'
            )}
          >
            {collapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <>
                <ChevronLeft className="w-5 h-5" />
                <span className="text-sm font-medium">{t('sidebar.collapse')}</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Language & Sound - Mobile only */}
      {isMobile && (
        <div className="px-4 py-3 border-t border-slate-200 space-y-3">
          <div className="px-4 py-3 rounded-xl bg-slate-50 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">
              {t('sidebar.language')}
            </span>
            {/* Simple inline selector to match header behavior */}
            <LanguageSelector size="sm" />
          </div>
          <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-slate-50">
            <div className="flex items-center gap-3">
              {soundEnabled ? (
                <Volume2 className="w-5 h-5 text-cyan-600" />
              ) : (
                <VolumeX className="w-5 h-5 text-slate-400" />
              )}
              <span className="text-sm font-medium text-slate-700">
                {t('sidebar.allowSound')}
              </span>
            </div>
            <Switch
              checked={soundEnabled}
              onCheckedChange={toggleSound}
              className="data-[state=checked]:bg-cyan-500"
            />
          </div>
        </div>
      )}

      {/* User Account */}
      <div className={cn(
        'p-4 border-t border-slate-200',
        collapsed && !isMobile && 'flex justify-center'
      )}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                'flex items-center gap-3 w-full rounded-xl transition-colors',
                'hover:bg-slate-100',
                collapsed && !isMobile ? 'justify-center p-2' : 'px-4 py-3'
              )}
            >
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-white">
                  {getInitials()}
                </span>
              </div>
              {(!collapsed || isMobile) && (
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-[#1E1B4B] truncate">
                    {user?.email?.split('@')[0]}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {user?.email}
                  </p>
                </div>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align={collapsed && !isMobile ? 'center' : 'start'} className="w-56">
            <DropdownMenuItem disabled className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span className="truncate">{user?.email}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut} className="text-red-600 focus:text-red-600">
              <LogOut className="w-4 h-4 mr-2" />
              {t('sidebar.signOut')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}
